import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Button, Badge } from './ui';
import massJoinService from '../services/massJoinService';
import {
  UsersIcon,
  PlusIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const MassGroupJoiningSection = ({ accountId, setError, setSuccess }) => {
  const queryClient = useQueryClient();
  const [links, setLinks] = useState('');
  const [options, setOptions] = useState({
    initialDelay: 30,
    maxRetries: 3,
    pauseBetweenGroups: 60,
    exponentialBackoff: true
  });
  const [expandedProcessId, setExpandedProcessId] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds

  // Fetch active processes
  const {
    data: processes,
    isLoading: processesLoading,
    refetch: refetchProcesses
  } = useQuery(
    ['mass-join-processes'],
    massJoinService.getAllMassJoinProcesses,
    {
      refetchInterval: refreshInterval,
      onError: (err) => {
        console.error('Error fetching mass join processes:', err);
      }
    }
  );

  // Filter processes for this account
  const accountProcesses = processes?.filter(process => process.accountId === parseInt(accountId)) || [];

  // Auto-stop refetching if no active processes
  useEffect(() => {
    if (accountProcesses.length === 0 || 
        accountProcesses.every(p => p.status === 'completed' || p.status === 'cancelled')) {
      setRefreshInterval(0); // Stop auto-refresh
    } else {
      setRefreshInterval(5000); // Keep refreshing every 5 seconds
    }
  }, [accountProcesses]);

  // Start mass join process mutation
  const startMassJoinMutation = useMutation(
    (data) => massJoinService.startMassJoin(accountId, data.links, data.options),
    {
      onSuccess: (data) => {
        setSuccess(`Mass join process started successfully. Process ID: ${data.processId}`);
        setLinks('');
        setRefreshInterval(5000); // Start auto-refresh
        queryClient.invalidateQueries(['mass-join-processes']);
      },
      onError: (err) => {
        setError(`Failed to start mass join process: ${err.message}`);
      }
    }
  );

  // Pause process mutation
  const pauseProcessMutation = useMutation(
    (processId) => massJoinService.pauseMassJoinProcess(processId),
    {
      onSuccess: () => {
        setSuccess('Process paused successfully');
        queryClient.invalidateQueries(['mass-join-processes']);
      },
      onError: (err) => {
        setError(`Failed to pause process: ${err.message}`);
      }
    }
  );

  // Resume process mutation
  const resumeProcessMutation = useMutation(
    (processId) => massJoinService.resumeMassJoinProcess(processId),
    {
      onSuccess: () => {
        setSuccess('Process resumed successfully');
        setRefreshInterval(5000); // Start auto-refresh
        queryClient.invalidateQueries(['mass-join-processes']);
      },
      onError: (err) => {
        setError(`Failed to resume process: ${err.message}`);
      }
    }
  );

  // Cancel process mutation
  const cancelProcessMutation = useMutation(
    (processId) => massJoinService.cancelMassJoinProcess(processId),
    {
      onSuccess: () => {
        setSuccess('Process cancelled successfully');
        queryClient.invalidateQueries(['mass-join-processes']);
      },
      onError: (err) => {
        setError(`Failed to cancel process: ${err.message}`);
      }
    }
  );

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate links
    if (!links.trim()) {
      setError('Please enter at least one group link');
      return;
    }
    
    // Parse links from textarea (one per line)
    const linkArray = links
      .split('\n')
      .map(link => link.trim())
      .filter(Boolean);
    
    if (linkArray.length === 0) {
      setError('Please enter at least one group link');
      return;
    }
    
    // Start the mass join process
    startMassJoinMutation.mutate({
      links: linkArray,
      options
    });
  };

  // Format time remaining
  const formatTimeRemaining = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const now = new Date();
    const target = new Date(timestamp);
    const diffSeconds = Math.max(0, Math.floor((target - now) / 1000));
    
    if (diffSeconds === 0) return 'Now';
    
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="blue">In Progress</Badge>;
      case 'paused':
        return <Badge variant="yellow">Paused</Badge>;
      case 'completed':
        return <Badge variant="green">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="gray">Cancelled</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  // Handle option changes
  const handleOptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOptions(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value, 10)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Add Multiple Groups</h4>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="links" className="block text-sm font-medium text-gray-700 mb-1">
              Group Links (one per line)
            </label>
            <textarea
              id="links"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://t.me/group1&#10;https://t.me/group2&#10;@group3"
              rows="5"
              value={links}
              onChange={(e) => setLinks(e.target.value)}
            ></textarea>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pauseBetweenGroups" className="block text-sm font-medium text-gray-700 mb-1">
                Pause Between Groups (seconds)
              </label>
              <input
                type="number"
                id="pauseBetweenGroups"
                name="pauseBetweenGroups"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="10"
                max="3600"
                value={options.pauseBetweenGroups}
                onChange={handleOptionChange}
              />
              <p className="mt-1 text-xs text-gray-500">
                Longer pauses (60-180s) reduce the risk of rate limits
              </p>
            </div>
            
            <div>
              <label htmlFor="maxRetries" className="block text-sm font-medium text-gray-700 mb-1">
                Max Retries Per Link
              </label>
              <input
                type="number"
                id="maxRetries"
                name="maxRetries"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="1"
                max="10"
                value={options.maxRetries}
                onChange={handleOptionChange}
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="exponentialBackoff"
              name="exponentialBackoff"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={options.exponentialBackoff}
              onChange={handleOptionChange}
            />
            <label htmlFor="exponentialBackoff" className="ml-2 block text-sm text-gray-700">
              Use exponential backoff (increase delay after errors)
            </label>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              icon={UsersIcon}
              isLoading={startMassJoinMutation.isLoading}
              disabled={startMassJoinMutation.isLoading || !links.trim()}
            >
              Start Mass Join Process
            </Button>
          </div>
        </form>
      </div>

      {/* Active Processes */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Active Processes</h4>
        
        {processesLoading ? (
          <p className="text-center text-gray-500 py-4">
            Loading processes...
          </p>
        ) : accountProcesses.length === 0 ? (
          <p className="text-center text-gray-500 py-4">
            No active mass join processes for this account.
          </p>
        ) : (
          <div className="space-y-4">
            {accountProcesses.map(process => (
              <div key={process.processId} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <UsersIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <div>
                      <h5 className="text-sm font-medium text-gray-700">
                        Process {process.processId.substring(0, 8)}...
                      </h5>
                      <p className="text-xs text-gray-500">
                        Started: {new Date(process.started).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(process.status)}
                    <button
                      onClick={() => setExpandedProcessId(expandedProcessId === process.processId ? null : process.processId)}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                    >
                      {expandedProcessId === process.processId ? (
                        <span className="text-xs">Collapse</span>
                      ) : (
                        <span className="text-xs">Expand</span>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-gray-500">Progress: </span>
                      <span className="font-medium">
                        {process.processed}/{process.totalLinks} ({Math.round((process.processed / process.totalLinks) * 100)}%)
                      </span>
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-primary-600 h-2.5 rounded-full"
                          style={{ width: `${Math.round((process.processed / process.totalLinks) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      {process.status === 'in_progress' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={PauseIcon}
                          onClick={() => pauseProcessMutation.mutate(process.processId)}
                          isLoading={pauseProcessMutation.isLoading}
                          disabled={pauseProcessMutation.isLoading}
                        >
                          Pause
                        </Button>
                      )}
                      
                      {process.status === 'paused' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={PlayIcon}
                          onClick={() => resumeProcessMutation.mutate(process.processId)}
                          isLoading={resumeProcessMutation.isLoading}
                          disabled={resumeProcessMutation.isLoading}
                        >
                          Resume
                        </Button>
                      )}
                      
                      {(process.status === 'in_progress' || process.status === 'paused') && (
                        <Button
                          variant="danger"
                          size="sm"
                          icon={StopIcon}
                          onClick={() => {
                            if (window.confirm('Are you sure you want to cancel this process?')) {
                              cancelProcessMutation.mutate(process.processId);
                            }
                          }}
                          isLoading={cancelProcessMutation.isLoading}
                          disabled={cancelProcessMutation.isLoading}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-2 flex text-sm text-gray-500 space-x-4">
                    <div><span className="font-medium text-green-600">{process.successful}</span> successful</div>
                    <div><span className="font-medium text-red-600">{process.failed}</span> failed</div>
                    <div><span className="font-medium text-gray-600">{process.skipped}</span> skipped</div>
                    {process.status === 'in_progress' && process.nextAttemptAt && (
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span>Next: {formatTimeRemaining(process.nextAttemptAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {expandedProcessId === process.processId && (
                  <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <h6 className="text-xs font-medium text-gray-700 mb-2">Links Status</h6>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Link
                            </th>
                            <th scope="col" className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Result
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {process.links.map((link, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-800">
                                {link.url}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-xs">
                                {link.status === 'joined' && (
                                  <span className="inline-flex items-center text-green-700">
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    Joined
                                  </span>
                                )}
                                {link.status === 'request_sent' && (
                                  <span className="inline-flex items-center text-blue-700">
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    Request Sent
                                  </span>
                                )}
                                {link.status === 'kicked' && (
                                  <span className="inline-flex items-center text-orange-700">
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    Kicked
                                  </span>
                                )}
                                {link.status === 'failed' && (
                                  <span className="inline-flex items-center text-red-700">
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    Failed
                                  </span>
                                )}
                                {link.status === 'pending' && (
                                  <span className="inline-flex items-center text-gray-600">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Pending
                                  </span>
                                )}
                                {link.status === 'skipped' && (
                                  <span className="inline-flex items-center text-gray-500">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Skipped
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-xs text-gray-500">
                                {link.chatTitle && (
                                  <span>
                                    {link.chatTitle}
                                  </span>
                                )}
                                {link.errorMessage && (
                                  <span className="text-red-600">
                                    {link.errorMessage.substring(0, 50)}
                                    {link.errorMessage.length > 50 ? '...' : ''}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {process.logs && process.logs.length > 0 && (
                      <>
                        <h6 className="text-xs font-medium text-gray-700 mt-4 mb-2">Recent Logs</h6>
                        <div className="bg-gray-800 text-gray-200 p-2 rounded text-xs overflow-y-auto max-h-40 font-mono">
                          {process.logs.slice(-10).map((log, index) => (
                            <div key={index}>
                              <span className="text-gray-400">[{new Date(log.time).toLocaleTimeString()}]</span> {log.message}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {accountProcesses.length > 0 && (
          <div className="mt-2 text-right">
            <button
              onClick={refetchProcesses}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MassGroupJoiningSection;
